import {
  Heart,
  GraduationCap,
  Sprout,
  Building2,
  Users2,
  Award,
  ArrowRight,
  Briefcase,
  ShieldCheck,
  Globe2,
  TreeDeciduous,
  Cpu,
  Stethoscope,
  HandHeart,
  Quote,
  CheckCircle2,
  Mail,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PolicyPageShell } from "@/components/PolicyHeader";
import { SITE_IMAGES } from "@/data/site-images";

const FOUR_PILLARS = [
  { t: "Responsibility", d: "Accountable to communities, customers and the nation" },
  { t: "Sustainability", d: "Programs designed to create long-term value, not one-off gestures" },
  { t: "Inclusiveness", d: "Reaching tribal, rural and underserved populations first" },
  { t: "Long-Term Impact", d: "Measured by lives improved, not budgets spent" },
];

const FOCUS_AREAS = [
  {
    icon: GraduationCap,
    title: "Financial Literacy & Inclusion",
    accent: "#D4AF37",
    lead: "Financial knowledge is the foundation of financial independence.",
    items: [
      "Personal Finance",
      "Budget Planning",
      "Savings & Investments",
      "Responsible Borrowing",
      "Digital Banking",
      "Credit Awareness",
      "Insurance Awareness",
      "Fraud Prevention",
      "Financial Planning",
      "Retirement Planning",
    ],
    closer: "Our objective is to make every family financially aware and financially secure.",
  },
  {
    icon: Award,
    title: "Education & Skill Development",
    accent: "#F0D788",
    lead: "Education creates opportunities.",
    items: [
      "Student Scholarships",
      "Digital Learning",
      "Career Guidance",
      "Professional Skill Development",
      "Financial Education",
      "Entrepreneurship Training",
      "Computer Literacy",
      "Competitive Exam Support",
      "Career Counseling",
      "Digital Skills",
    ],
    closer: "Investing in education is investing in the future of our nation.",
  },
  {
    icon: Briefcase,
    title: "Entrepreneurship & MSME Development",
    accent: "#5DADE2",
    lead: "Small businesses are the backbone of India's economy.",
    items: [
      "Business Guidance",
      "Startup Mentorship",
      "Financial Awareness",
      "Project Planning",
      "Business Development Programs",
      "MSME Support",
      "Women Entrepreneurs",
      "Rural Entrepreneurs",
      "Self-Employment Programs",
      "Youth Entrepreneurship",
    ],
  },
  {
    icon: HandHeart,
    title: "Women Empowerment",
    accent: "#E879A6",
    lead: "Committed to supporting women in becoming financially independent.",
    items: [
      "Women Entrepreneurship",
      "Financial Literacy",
      "Business Skill Development",
      "Self-Help Group Support",
      "Employment Opportunities",
      "Leadership Development",
      "Digital Skills Training",
      "Financial Inclusion Programs",
    ],
  },
  {
    icon: Users2,
    title: "Youth Development",
    accent: "#9B7CB9",
    lead: "India's future depends on its youth.",
    items: [
      "Leadership Development",
      "Innovation",
      "Professional Skills",
      "Employment Readiness",
      "Career Development",
      "Entrepreneurship",
      "Digital Literacy",
      "Financial Awareness",
      "Personality Development",
    ],
  },
  {
    icon: Sprout,
    title: "Rural & Tribal Development",
    accent: "#7CB342",
    lead: "Inclusive growth begins in rural India.",
    items: [
      "Financial Inclusion",
      "Agriculture Awareness",
      "Farmer Development",
      "Rural Entrepreneurship",
      "Livelihood Enhancement",
      "Tribal Welfare",
      "Community Infrastructure",
      "Digital Banking Awareness",
      "Self-Employment Opportunities",
    ],
  },
  {
    icon: Stethoscope,
    title: "Healthcare & Community Welfare",
    accent: "#E57373",
    lead: "Healthy communities create stronger economies.",
    items: [
      "Health Awareness",
      "Medical Camps",
      "Blood Donation Drives",
      "Preventive Healthcare",
      "Nutrition Awareness",
      "Mental Health Awareness",
      "Community Wellness",
      "Emergency Relief",
    ],
  },
  {
    icon: TreeDeciduous,
    title: "Environmental Sustainability",
    accent: "#66BB6A",
    lead: "Protecting nature is everyone's responsibility.",
    items: [
      "Tree Plantation",
      "Water Conservation",
      "Clean Energy Awareness",
      "Waste Management",
      "Plastic-Free Campaigns",
      "Green Office Practices",
      "Climate Awareness",
      "Sustainable Development",
      "Carbon Footprint Reduction",
    ],
  },
  {
    icon: Cpu,
    title: "Digital Inclusion",
    accent: "#42A5F5",
    lead: "Technology should benefit everyone.",
    items: [
      "Digital Financial Services",
      "Cyber Security Awareness",
      "Digital Literacy",
      "Safe Online Banking",
      "Cashless Economy",
      "Digital Payments",
      "AI Awareness",
      "Responsible Technology Usage",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Ethical Business Practices",
    accent: "#FFA726",
    lead: "Our commitment to society begins within our organization.",
    items: [
      "Integrity",
      "Transparency",
      "Fair Business Practices",
      "Customer Protection",
      "Regulatory Compliance",
      "Equal Opportunity Employment",
      "Anti-Corruption Policies",
      "Data Privacy",
      "Responsible Governance",
    ],
  },
];

const VOLUNTEERING = [
  "Volunteer Programs",
  "Community Service",
  "Awareness Campaigns",
  "Charity Initiatives",
  "Educational Support",
  "Disaster Relief Activities",
];

const SDGS = [
  "No Poverty",
  "Zero Hunger",
  "Good Health & Well-being",
  "Quality Education",
  "Gender Equality",
  "Clean Water & Sanitation",
  "Decent Work & Economic Growth",
  "Industry, Innovation & Infrastructure",
  "Reduced Inequalities",
  "Sustainable Cities & Communities",
  "Responsible Consumption",
  "Climate Action",
  "Partnerships for the Goals",
];

function Section({ eyebrow, title, lead, children, testId }) {
  return (
    <section data-testid={testId} className="py-14 md:py-16 border-t border-white/5">
      <div>
        {eyebrow && (
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
            {eyebrow}
          </div>
        )}
        {title && (
          <h2 className="font-serif text-3xl md:text-4xl text-white max-w-3xl leading-tight">
            {title}
          </h2>
        )}
        {lead && (
          <p className="mt-4 text-base text-white/75 max-w-3xl leading-relaxed">{lead}</p>
        )}
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

export default function CSR() {
  return (
    <PolicyPageShell
      testId="csr-page"
      eyebrow="Corporate Social Responsibility"
      title={
        <>
          Empowering <em className="not-italic text-[#D4AF37]">Communities</em>. Creating{" "}
          <span className="font-script italic text-[#F0D788]">Opportunities</span>. Building a
          Better Tomorrow.
        </>
      }
      lead="At Sahyog Finance & Noble Infinity Asset Management Private Limited, CSR is not merely a legal obligation — it is a fundamental part of our identity, our values and our long-term vision."
    >
      {/* Intro */}
      <Section testId="csr-intro">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-start">
          <div className="space-y-5 text-base text-white/80 leading-relaxed">
            <p>
              The true success of any organization is measured not only by its financial
              performance but also by the positive impact it creates in society. Our commitment
              extends beyond providing financial services — we strive to improve lives, strengthen
              communities, promote financial inclusion and contribute to India's sustainable
              development.
            </p>
            <p>
              Every initiative we undertake is guided by our belief that businesses should create
              shared value for customers, employees, communities and future generations.
            </p>
          </div>
          <div className="rounded-3xl overflow-hidden border border-[#D4AF37]/20">
            <img
              src={SITE_IMAGES.heroes[8]}
              alt="Empowering communities · sustainable growth"
              loading="lazy"
              data-testid="csr-intro-image"
              className="w-full h-[240px] md:h-[300px] object-cover"
            />
          </div>
        </div>
      </Section>

      {/* CSR Vision */}
      <Section testId="csr-vision" eyebrow="Our CSR Vision">
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-12 max-w-4xl">
          <Quote size={26} className="text-[#D4AF37] mb-5" />
          <div className="font-serif text-xl md:text-2xl leading-snug text-white">
            "To build an inclusive, financially empowered, educated, healthy and{" "}
            <span className="text-[#F0D788]">sustainable society</span> where every individual
            has the opportunity to achieve a better quality of life."
          </div>
          <p className="mt-6 text-sm text-white/65 leading-relaxed">
            A future where economic growth goes hand in hand with social progress, environmental
            responsibility, ethical governance and community development.
          </p>
        </div>
      </Section>

      {/* CSR Mission */}
      <Section
        testId="csr-mission"
        eyebrow="Our CSR Mission"
        title="Meaningful initiatives, strategic partnerships, sustainable programs"
        lead="Actively contribute to the social, educational, environmental and economic development of communities — supporting financial literacy, education, healthcare, entrepreneurship, environmental conservation, women empowerment, youth development, tribal welfare and rural transformation."
      >
        <div></div>
      </Section>

      {/* CSR Philosophy + Four Pillars */}
      <Section testId="csr-philosophy" eyebrow="Our CSR Philosophy">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 items-start">
          <div className="space-y-3 max-w-md">
            <p className="font-serif text-lg text-white leading-snug">
              <span className="text-[#F0D788]">A financially strong nation</span> begins with
              financially empowered people.
            </p>
            <p className="font-serif text-lg text-white leading-snug">
              <span className="text-[#F0D788]">A successful business</span> has a responsibility
              to uplift society.
            </p>
            <p className="font-serif text-lg text-white leading-snug">
              <span className="text-[#F0D788]">Growth becomes meaningful</span> only when it is
              inclusive.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FOUR_PILLARS.map((p, i) => (
              <div
                key={p.t}
                data-testid={`csr-pillar-${i}`}
                className="rounded-2xl border border-[#D4AF37]/20 bg-[#0A1F44] p-5"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#F0D788] mb-2">
                  Pillar {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif text-lg text-white mb-1.5">{p.t}</div>
                <div className="text-sm text-white/70 leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Focus Areas */}
      <Section
        testId="csr-focus-areas"
        eyebrow="Our Focus Areas"
        title="10 priority areas where Sahyog Finance & Noble Infinity creates impact"
      >
        <div className="mb-8 rounded-3xl overflow-hidden border border-[#D4AF37]/20">
          <img
            src={SITE_IMAGES.heroes[5]}
            alt="Inclusive growth · 10 priority focus areas"
            loading="lazy"
            data-testid="csr-focus-image"
            className="w-full h-[180px] md:h-[240px] object-cover"
          />
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {FOCUS_AREAS.map((f, i) => (
            <div
              key={f.title}
              data-testid={`csr-focus-${i}`}
              className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6 hover:border-[#D4AF37]/45 transition"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-11 h-11 grid place-items-center rounded-xl"
                  style={{
                    background: `${f.accent}18`,
                    border: `1px solid ${f.accent}45`,
                  }}
                >
                  <f.icon size={20} style={{ color: f.accent }} />
                </div>
                <h3 className="font-serif text-lg text-white">{f.title}</h3>
              </div>
              {f.lead && (
                <p className="text-sm text-white/60 italic mb-3 leading-relaxed">{f.lead}</p>
              )}
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {f.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-white/80">
                    <CheckCircle2 size={11} className="text-[#D4AF37] mt-1.5 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
              {f.closer && (
                <p className="mt-4 text-xs text-white/55 leading-relaxed border-t border-white/10 pt-3">
                  {f.closer}
                </p>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* Employee Volunteering */}
      <Section
        testId="csr-volunteering"
        eyebrow="Employee Volunteering"
        title="Greater impact, together"
        lead="We encourage our employees, business partners and associates to actively participate in social development activities."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {VOLUNTEERING.map((v, i) => (
            <div
              key={v}
              data-testid={`csr-volunteer-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <HandHeart size={14} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm text-white/85">{v}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* SDGs */}
      <Section
        testId="csr-sdgs"
        eyebrow="UN Sustainable Development Goals"
        title="Our CSR initiatives aligned with global priorities"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {SDGS.map((s, i) => (
            <div
              key={s}
              data-testid={`csr-sdg-${i}`}
              className="flex items-center gap-3 rounded-xl border border-[#D4AF37]/15 bg-[#0A1F44] px-4 py-3"
            >
              <Globe2 size={14} className="text-[#F0D788] shrink-0" />
              <div className="text-sm text-white/85">{s}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* CSR Promise */}
      <Section testId="csr-promise" eyebrow="Our CSR Promise">
        <div className="rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-8 md:p-10 max-w-4xl">
          <p className="text-base md:text-lg text-white/85 leading-relaxed">
            We are committed to creating <strong className="text-white">measurable, meaningful
            and sustainable</strong> social impact through responsible corporate citizenship. Our
            objective is not only to grow as a business but also to contribute towards building a
            stronger, more inclusive, environmentally responsible and financially empowered India.
          </p>
          <p className="mt-4 text-sm text-white/65 italic">
            Every initiative we undertake reflects our belief that business success and social
            progress must grow together.
          </p>
        </div>
      </Section>

      {/* Join us */}
      <Section
        testId="csr-collaborate"
        eyebrow="Join Us in Creating Positive Change"
        title="Collaborate with us"
        lead="We welcome NGOs, educational institutions, government organizations, social enterprises, community groups, volunteers and responsible citizens to collaborate with us. Together, we can empower lives, transform communities and build a brighter future."
      >
        <div className="flex flex-wrap items-center gap-3">
          <a
            href="mailto:info.nobleinfinity@gmail.com?subject=CSR%20Partnership%20Proposal"
            data-testid="csr-email-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"
          >
            <Mail size={14} /> Propose a CSR partnership
          </a>
          <Link
            to="/customer-services"
            data-testid="csr-contact-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-outline-gold text-sm"
          >
            Contact us <ArrowRight size={14} />
          </Link>
        </div>
      </Section>

      {/* CSR Motto */}
      <Section testId="csr-motto" eyebrow="Our CSR Motto">
        <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-14 text-center max-w-4xl mx-auto">
          <Heart size={26} className="text-[#D4AF37] mx-auto mb-4" />
          <div className="font-serif text-2xl md:text-4xl text-white leading-tight">
            Empowering Communities ·{" "}
            <span className="text-[#F0D788]">Enriching Lives</span> ·{" "}
            <span className="font-script italic text-[#F0D788]">Creating Sustainable Futures.</span>
          </div>
        </div>
      </Section>

      {/* Disclaimer */}
      <div className="mt-10 text-[11px] text-white/45 leading-relaxed">
        <strong className="text-white/65">Disclaimer:</strong> Sahyog Finance &amp; Noble
        Infinity Asset Management Private Limited undertakes Corporate Social Responsibility
        initiatives in accordance with applicable laws, regulatory requirements and the
        Company's policies. CSR activities may evolve over time based on community needs,
        strategic priorities and statutory provisions.
      </div>
    </PolicyPageShell>
  );
}
